import { cn } from "../shared/ui/index.ts";
import * as React from "react";
import { I } from "../shared/icons/icons.tsx";
import {
  inputPlain,
  miniIconBtn,
  toolbarSearch,
  mobileListCard,
  pg,
  pgBarActions,
  accentBtnLg,
} from "../shared/page-layout.ts";
import { appOpenPatient } from "../core/app-bridge.ts";
import { PatientAvatar } from "../shared/patient/patient-avatar.component.tsx";
import { PatientNameDisplay } from "../shared/patient/patient-name-display.component.tsx";
import {
  comparePatientsBySearch,
  patientMatchesSearch,
} from "../shared/patient/patient-name.ts";
import { searchIncludes } from "../shared/text-search.ts";
import { usePractice } from "../core/providers/practice.provider.tsx";
import { patientsService } from "../core/services/patients.service.ts";
import type { Patient } from "../core/services/patients.service.ts";

function openPatient(p: Patient) {
  appOpenPatient(p);
}

export function PatientsPage() {
  const { practice } = usePractice();
  const [query, setQuery] = React.useState("");
  const q = query.trim();
  const filtered = patientsService
    .list(practice.uuid)
    .filter(
      (p) => !q || patientMatchesSearch(p.name, q) || searchIncludes(p.tag, q),
    )
    .sort((a, b) => comparePatientsBySearch(a.name, b.name, q));

  return (
    <div className={pg.page}>
      <div className={pg.toolbar}>
        <div className={toolbarSearch}>
          <I.Search size={14} className="shrink-0 text-sf-text-muted" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Rechercher un patient…"
            className={cn(inputPlain, "text-[13px]")}
          />
        </div>
        <div className={pgBarActions}>
          <button type="button" className={accentBtnLg()}>
            <I.UserPlus size={14} /> Nouveau patient
          </button>
        </div>
      </div>

      <div className={pg.content}>
        <div className="overflow-hidden rounded-lg border border-sf-border bg-white">
          <div className="hidden border-b border-sf-border bg-sf-surface-alt px-4 py-2.5 text-[11px] font-semibold uppercase tracking-wider text-sf-text-muted md:grid md:grid-cols-[44px_1.4fr_80px_70px_1.2fr_1fr_1fr_auto] md:gap-3.5">
            <div />
            <div>Patient</div>
            <div>Âge</div>
            <div>Sexe</div>
            <div>Téléphone</div>
            <div>Dernier RDV</div>
            <div>Suivi</div>
            <div />
          </div>

          {filtered.map((p, i) => (
            <PatientRow key={p.uuid} patient={p} bordered={i > 0} />
          ))}

          {filtered.length === 0 && (
            <div className="px-4 py-10 text-center text-[13px] text-sf-text-muted">
              Aucun patient trouvé
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function PatientRow({
  patient: p,
  bordered,
}: {
  patient: Patient;
  bordered: boolean;
}) {
  const rowProps = {
    role: "button" as const,
    tabIndex: 0,
    onClick: () => openPatient(p),
    onKeyDown: (e: React.KeyboardEvent) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        openPatient(p);
      }
    },
  };

  return (
    <>
      <button
        type="button"
        {...rowProps}
        className={cn(
          mobileListCard(),
          "md:hidden",
          bordered && "border-t border-sf-border",
        )}
      >
        <PatientAvatar patient={p} size={40} />
        <div className="min-w-0 flex-1">
          <PatientNameDisplay
            name={p.name}
            firstClassName="text-[15px] font-semibold"
          />
          <div className="mt-0.5 truncate text-xs text-sf-text-muted">
            {p.age} ans · {p.sex} · {p.phone}
          </div>
          <span className="mt-1.5 inline-flex max-w-full truncate rounded border border-sf-primary-100 bg-sf-primary-50 px-2 py-0.5 text-[11px] font-semibold text-sf-primary">
            {p.tag}
          </span>
        </div>
        <I.ChevronRight size={16} className="shrink-0 text-sf-text-muted" />
      </button>

      <div
        {...rowProps}
        className={cn(
          "hidden cursor-pointer md:grid md:grid-cols-[44px_1.4fr_80px_70px_1.2fr_1fr_1fr_auto] md:items-center md:gap-3.5 md:px-4 md:py-3 md:text-[13px] md:hover:bg-gray-50",
          bordered && "md:border-t md:border-sf-border",
        )}
      >
        <PatientAvatar patient={p} size={32} />
        <PatientNameDisplay name={p.name} firstClassName="font-semibold" />
        <div className="text-sf-text-secondary">{p.age} ans</div>
        <div className="text-sf-text-secondary">{p.sex}</div>
        <div className="tabular-nums text-sf-text-secondary">{p.phone}</div>
        <div className="text-sf-text-secondary">{p.last}</div>
        <div>
          <span className="rounded border border-sf-primary-100 bg-sf-primary-50 px-2 py-0.5 text-[11px] font-semibold text-sf-primary">
            {p.tag}
          </span>
        </div>
        <div>
          <button
            type="button"
            className={miniIconBtn()}
            onClick={(e) => {
              e.stopPropagation();
              openPatient(p);
            }}
          >
            <I.ChevronRight size={13} />
          </button>
        </div>
      </div>
    </>
  );
}
